/* eslint-disable @typescript-eslint/no-explicit-any */

import { BayLevelEnum, ForeAftEnum, IBayLevelData } from "open-vessel-definition";

import { IBayPattern } from "./types/IPositionPatterns";
import { getPairedIsoBay } from "./getPairedIsoBay";
import { getSizesFromSlots } from "./getSizesFromSlots";

/**
 * Minimum container length, in feet, that needs two paired bays to be stowed.
 * 40', 45', 48' and 53' qualify; 20' and 24' do not.
 */
export const MIN_PAIRED_BAY_LENGTH = 40;

/** Why a Bay cannot hold 40'+ definitions */
export type TBay40sBlockedReason =
  /** The Bay declares no pairing at all */
  | "notPaired"
  /** The Bay is the wrong half of its pair for this vessel's convention */
  | "wrongSide";

export interface IBay40sAllowance {
  allowed: boolean;
  reason?: TBay40sBlockedReason;
  /** The Bay the 40'+ belong in, when `reason === "wrongSide"` */
  pairedIsoBay?: IBayPattern;
  /** The convention applied to reach this verdict */
  location?: ForeAftEnum;
}

const ALLOWED: IBay40sAllowance = { allowed: true };

/**
 * Can 40'+ containers be defined in this Bay?
 *
 * A 40'+ container spans two ISO bays, so its definition belongs in one half of a
 * pair only, and only when the Bay is paired at all. Which half is an authoring
 * convention the vessel records in `shipData.bay40sLocation`.
 *
 * Remember that {@link IBayLevelData.pairedBay} names the *other* Bay of the pair:
 * a Bay declaring `AFT` is itself the FWD one. `bay40sLocation` is not reversed, it
 * names the Bay that holds the data.
 *
 * @param isoBay the Bay being edited
 * @param level ABOVE or BELOW
 * @param pairedBay the Bay's own `pairedBay`, as declared (not re-derived from `bls`,
 *   so that a trailing Bay whose partner was never created still counts as paired)
 * @param bay40sLocation the vessel's convention. Pass it already resolved: callers
 *   that have no `shipData.bay40sLocation` should fall back to
 *   `detectBay40sLocation(bls).bay40sLocation` once, not per call. `undefined` means
 *   no convention could be established, and only the pairing rule applies.
 * @param bls the vessel's `baysData`, used to look the paired Bay up
 */
export function can40sBeDefinedInBay(
  isoBay: IBayPattern,
  level: BayLevelEnum,
  pairedBay: ForeAftEnum | undefined,
  bay40sLocation: ForeAftEnum | undefined,
  bls: IBayLevelData[],
): IBay40sAllowance {
  if (!pairedBay) return { allowed: false, reason: "notPaired" };

  // `pairedBay` names the other Bay, so this Bay is the opposite one
  const currentSide = pairedBay === ForeAftEnum.AFT ? ForeAftEnum.FWD : ForeAftEnum.AFT;

  if (bay40sLocation === undefined) return ALLOWED;
  if (currentSide === bay40sLocation) return { allowed: true, location: bay40sLocation };

  const pairedIsoBay = getPairedIsoBay(isoBay, pairedBay);

  // The trailing pair: a vessel's last Bay has no 20' stack aft of it, so files
  // routinely keep the 40s in that Bay and never create the one it extends into.
  // Such a pair cannot follow the AFT convention, so let its FWD half hold them.
  if (bay40sLocation === ForeAftEnum.AFT && currentSide === ForeAftEnum.FWD) {
    const pairedBl = bls.find((bl) => bl.isoBay === (pairedIsoBay as any) && bl.level === level);

    if (pairedBl === undefined || getSizesFromSlots(pairedBl.perSlotInfo).definedSlots === 0)
      return { allowed: true, location: bay40sLocation };
  }

  return {
    allowed: false,
    reason: "wrongSide",
    pairedIsoBay,
    location: bay40sLocation,
  };
}
